import { ReviewItemProps } from '@src/quince-components/src'
import { getReviewImageAltText } from '@src/quince-site-pdp/src/atoms/reviewPopUp/utils'

export enum ReviewOptionType {
  CUSTOMER_PROFILE = 'dropdown',
  PRODUCT_PROFILE = 'slider',
}

export enum SummaryReviewOptionType {
  CUSTOMER_PROFILE = 'CUSTOMER_PROFILE',
  PRODUCT_PROFILE = 'PRODUCT_PROFILE',
}

export interface IReviewOption {
  readonly attributeType: ReviewOptionType
  readonly attributeName: string
  readonly displayName: string
  readonly percentage?: number
  readonly value: string
}

export interface ISummaryReviewOption {
  readonly name: string
  readonly value: string
  readonly rangePercent?: number
  readonly rangeOptions?: string[]
  readonly type: SummaryReviewOptionType
}

// Type definition for review tags
export interface IReviewTag {
  readonly tagValues?: string[]
  readonly tags?: string
}

export class ReviewOption implements IReviewOption {
  readonly attributeType: ReviewOptionType
  readonly attributeName: string
  readonly displayName: string
  readonly percentage: number | undefined
  readonly value: string

  constructor({ attributeType, attributeName, displayName, percentage, value }: IReviewOption) {
    this.attributeType = attributeType
    this.attributeName = attributeName
    this.displayName = displayName
    this.percentage = percentage
    this.value = value
    return {
      ...this,
    }
  }
}

export class SummaryReviewOption implements ISummaryReviewOption {
  readonly name: string
  readonly rangeOptions: string[] | undefined
  readonly rangePercent: number | undefined
  readonly type: SummaryReviewOptionType
  readonly value: string

  constructor({ name, value, rangePercent, rangeOptions, type }: ISummaryReviewOption) {
    this.name = name
    this.value = value
    this.rangePercent = rangePercent
    this.rangeOptions = rangeOptions
    this.type = type
    return {
      ...this,
    }
  }
}

interface IProductReview {
  id: number
  rating: number
  title: string
  author: string
  productId: string
  productHandle?: string
  createdAt: string
  body: string
  reviewOptions?: IReviewOption[]
  location?: string
  productTitle: string
  contentfulProductHandle?: null | string
  imagesFileUrls?: null | string[]
  source?: string
  reviewTags?: IReviewTag[]
}

export default class ProductReview implements IProductReview {
  readonly id: number
  readonly rating: number
  readonly title: string
  readonly author: string
  readonly productId: string
  readonly productHandle?: string
  readonly createdAt: string
  readonly body: string
  readonly reviewOptions?: any[] | undefined
  readonly location?: string
  readonly productTitle: string
  readonly contentfulProductHandle?: string | null | undefined
  readonly imagesFileUrls?: string[] | null | undefined
  readonly source?: string
  readonly reviewTags?: IReviewTag[]

  constructor({
    id,
    rating,
    title,
    author,
    productId,
    productHandle,
    createdAt,
    body,
    reviewOptions,
    location,
    productTitle,
    contentfulProductHandle,
    imagesFileUrls,
    source,
    reviewTags,
  }: IProductReview) {
    this.id = id
    this.rating = rating
    this.title = title
    this.author = author
    this.productId = productId
    this.productHandle = productHandle
    this.createdAt = createdAt
    this.body = body
    this.reviewOptions = reviewOptions
    this.location = location
    this.productTitle = productTitle
    this.contentfulProductHandle = contentfulProductHandle
    this.imagesFileUrls = imagesFileUrls
    this.source = source
    this.reviewTags = reviewTags

    return {
      ...this,
    }
  }

  static getProductReviewPropFromReview = (
    review: ProductReview,
    productTitle = '',
    showBundleProductTitle = false,
  ): ReviewItemProps => {
    return {
      reviewId: review.id,
      author: review.author,
      date: review.createdAt,
      reviewOptions: review.reviewOptions,
      rating: review.rating,
      title: review.title,
      body: review.body,
      productTitle: review.productTitle,
      showProductTitle: showBundleProductTitle,
      reviewImageProps: review.imagesFileUrls?.map(fileUrl => {
        return {
          url: fileUrl,
          title: productTitle,
          alt: getReviewImageAltText({ reviewTitle: review.title, productTitle: productTitle }),
        }
      }),
      source: review.source,
      brandName: review.reviewTags?.filter(x => x.tags === 'EXTERNAL_BRAND_NAME')?.[0]?.tagValues?.join(', '),
    }
  }
}

interface IProductReviewSummary {
  readonly reviewCount: number
  readonly averageRating: number
  readonly shopifyProductId?: number
  readonly reviewOptions?: ISummaryReviewOption[]
}

export class ProductReviewSummary implements IProductReviewSummary {
  readonly reviewCount: number
  readonly averageRating: number
  readonly shopifyProductId?: number
  readonly reviewOptions: SummaryReviewOption[] | undefined
  readonly totalCount?: number | undefined

  constructor({ reviewCount, averageRating, shopifyProductId, reviewOptions }: IProductReviewSummary) {
    this.reviewCount = reviewCount
    this.averageRating = averageRating
    this.shopifyProductId = shopifyProductId
    this.reviewOptions = reviewOptions?.map(ro => new SummaryReviewOption(ro))
    return {
      ...this,
    }
  }
}
